<template>
  <div class="fha-mip-page">
    <div class="page-header">
      <PageHeaderPortal>
<div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-building-shield"></i>
        </div>

        <div>
          <h1 class="page-title">FHA MIP Payments</h1>
          <p class="page-subtitle">Combined UFMIP receipt and monthly premium processing queue</p>
        </div>
      </div>
      </PageHeaderPortal>

      <div class="header-actions">
        <select v-model="timeFrame" class="filter-select days-select" @change="loadData">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="last_90_days">Last 90 Days</option>
        </select>

        <button class="refresh-btn" @click="loadData" :disabled="loading">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-sections">
      <div class="summary-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-list-check section-icon"></i>
            <span class="section-title">Queue Overview</span>
          </div>
        </div>

        <div class="section-cards">
          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-table-rows summary-icon"></i>
              <span class="summary-label">Total Payments</span>
            </div>
            <div class="summary-value">{{ summary.total }}</div>
          </div>

          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-dollar-sign summary-icon"></i>
              <span class="summary-label">Total Amount</span>
            </div>
            <div class="summary-value">{{ formatMoney(summary.totalAmount) }}</div>
          </div>
        </div>
      </div>

      <div class="summary-section types-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-sparkles section-icon"></i>
            <span class="section-title">Breakdown</span>
          </div>
        </div>

        <div class="section-cards">
          <div class="summary-card approve">
            <div class="summary-card-header">
              <i class="fa-duotone fa-calendar-days summary-icon"></i>
              <span class="summary-label">Monthly</span>
            </div>
            <div class="summary-value">{{ summary.monthly }}</div>
          </div>

          <div class="summary-card request-only">
            <div class="summary-card-header">
              <i class="fa-duotone fa-file-invoice-dollar summary-icon"></i>
              <span class="summary-label">UFMIP</span>
            </div>
            <div class="summary-value">{{ summary.ufmip }}</div>
          </div>

          <div class="summary-card manual-review">
            <div class="summary-card-header">
              <i class="fa-duotone fa-circle-question summary-icon"></i>
              <span class="summary-label">Loans Not Found</span>
            </div>
            <div class="summary-value">{{ summary.notFound }}</div>
          </div>

          <div class="summary-card no-audit">
            <div class="summary-card-header">
              <i class="fa-duotone fa-triangle-exclamation summary-icon"></i>
              <span class="summary-label">Errors</span>
            </div>
            <div class="summary-value">{{ summary.errored }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="toolbar single-line-toolbar">
      <input
        v-model.trim="search"
        type="text"
        class="search-input"
        placeholder="Search loan #, borrower, case #, tracking id, description..."
      />

      <select v-model="typeFilter" class="filter-select toolbar-select">
        <option value="">All Types</option>
        <option value="Monthly">Monthly</option>
        <option value="UFMIP">UFMIP</option>
      </select>

      <select v-model="statusFilter" class="filter-select toolbar-select">
        <option value="">All Statuses</option>
        <option value="written">Fields Written</option>
        <option value="queued">Queued (Locked)</option>
        <option value="not_found">Loan Not Found</option>
        <option value="error">Error</option>
      </select>

      <select v-model="dryRunFilter" class="filter-select toolbar-select">
        <option value="">Live + Dry Runs</option>
        <option value="live">Live Only</option>
        <option value="dry">Dry Runs Only</option>
      </select>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div class="table-card-header">
        <div class="table-card-title-wrap">
          <i class="fa-duotone fa-clipboard-list-check table-card-icon"></i>
          <span class="table-card-title">FHA MIP Payment Queue</span>
        </div>

        <div class="table-card-count">
          {{ filteredRows.length }}
        </div>
      </div>

      <div v-if="loading" class="table-state">Loading FHA MIP payment queue...</div>
      <div v-else-if="errorMessage" class="table-state error">{{ errorMessage }}</div>
      <div v-else-if="pagedRows.length === 0" class="table-state">No FHA MIP payments found.</div>

      <div v-else class="table-wrap">
        <table class="locks-table">
          <thead>
            <tr>
              <th @click="setSort('processedatutc')">
                Processed
                <span>{{ sortIcon('processedatutc') }}</span>
              </th>
              <th @click="setSort('paymenttype')">
                Type
                <span>{{ sortIcon('paymenttype') }}</span>
              </th>
              <th @click="setSort('loannumber')">
                Loan #
                <span>{{ sortIcon('loannumber') }}</span>
              </th>
              <th @click="setSort('borrowername')">
                Borrower
                <span>{{ sortIcon('borrowername') }}</span>
              </th>
              <th @click="setSort('casenumber')">
                Case #
                <span>{{ sortIcon('casenumber') }}</span>
              </th>
              <th @click="setSort('amountpaid')">
                Amount
                <span>{{ sortIcon('amountpaid') }}</span>
              </th>
              <th @click="setSort('paymentdate')">
                Paid
                <span>{{ sortIcon('paymentdate') }}</span>
              </th>
              <th @click="setSort('description')">
                Detail
                <span>{{ sortIcon('description') }}</span>
              </th>
              <th>Status</th>
              <th>Docs</th>
              <th>Summary</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in pagedRows" :key="row.id">
              <td>{{ formatDateTime(row.processedatutc) }}</td>

              <td>
                <span class="type-pill" :class="row.paymenttype === 'Monthly' ? 'monthly' : 'ufmip'">
                  {{ row.paymenttype }}
                </span>
                <span v-if="row.dryrun" class="dry-run-tag" title="Dry run — nothing written">DRY</span>
              </td>

              <td>
                <div class="primary-text">{{ row.loannumber || '—' }}</div>
              </td>

              <td>{{ borrowerName(row) }}</td>
              <td class="compact-cell">{{ row.casenumber || '—' }}</td>
              <td class="amount-cell">{{ formatMoney(row.amountpaid) }}</td>
              <td class="compact-cell">{{ formatDate(row.paymentdate) }}</td>
              <td class="compact-cell">{{ row.description || '—' }}</td>

              <td>
                <span class="decision-pill" :class="statusClass(row)">
                  {{ statusLabel(row) }}
                </span>
              </td>

              <td class="docs-cell">
                <a
                  v-if="row.pdfurl"
                  :href="row.pdfurl"
                  target="_blank"
                  class="doc-btn"
                  title="Open batch receipt PDF"
                >
                  <i class="fa-duotone fa-file-pdf"></i>
                </a>
                <a
                  v-if="row.excelurl"
                  :href="row.excelurl"
                  target="_blank"
                  class="doc-btn excel"
                  title="Open batch accounting Excel"
                >
                  <i class="fa-duotone fa-file-excel"></i>
                </a>
                <span v-if="!row.pdfurl && !row.excelurl">—</span>
              </td>

              <td>
                <button class="summary-btn" @click="openSummaryModal(row)">
                  Summary
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredRows.length" class="pagination-bar">
      <div class="pagination-info">
        Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }}
      </div>

      <div class="pagination-actions">
        <button class="page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
        <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
        <button class="page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
      </div>
    </div>

    <!-- Summary Modal -->
    <div v-if="showSummaryModal" class="modal-backdrop" @click.self="closeSummaryModal">
      <div class="modal-card reason-modal-card">
        <div class="modal-header">
          <h3 class="modal-title">Payment Detail</h3>
          <button class="close-btn" @click="closeSummaryModal">✕</button>
        </div>

        <div class="modal-body reason-modal-body">
          <div class="reason-meta">
            <div><strong>Type:</strong> {{ selectedRow.paymenttype || '—' }}</div>
            <div><strong>Tracking ID:</strong> {{ selectedRow.trackingid || '—' }}</div>
            <div><strong>Loan #:</strong> {{ selectedRow.loannumber || selectedRow.loanid || '—' }}</div>
            <div><strong>Borrower:</strong> {{ borrowerName(selectedRow) }}</div>
            <div><strong>Case #:</strong> {{ selectedRow.casenumber || '—' }}</div>
            <div><strong>Amount:</strong> {{ formatMoney(selectedRow.amountpaid) }}</div>
            <div><strong>Payment Date:</strong> {{ formatDate(selectedRow.paymentdate) }}</div>
            <div v-if="selectedRow.paymenttype === 'Monthly'">
              <strong>Months Paid:</strong> {{ selectedRow.monthspaid ?? '—' }}
              <span v-if="selectedRow.latefeeassumed"> (+ Late Fee)</span>
            </div>
            <div><strong>Status:</strong> {{ statusLabel(selectedRow) }}</div>
            <div><strong>Receipt in eFolder:</strong> {{ selectedRow.receiptuploaded ? 'Yes' : 'No' }}</div>
            <div v-if="selectedRow.orgid"><strong>Org:</strong> {{ selectedRow.orgid }}</div>
            <div v-if="selectedRow.loanofficername"><strong>LO:</strong> {{ selectedRow.loanofficername }}</div>
          </div>

          <div class="reason-content">
            {{ selectedRow.description || '—' }}
          </div>

          <div class="doc-links" v-if="selectedRow.pdfurl || selectedRow.excelurl">
            <a v-if="selectedRow.pdfurl" :href="selectedRow.pdfurl" target="_blank" class="doc-link">
              <i class="fa-duotone fa-file-pdf"></i> Batch Receipt PDF
            </a>
            <a v-if="selectedRow.excelurl" :href="selectedRow.excelurl" target="_blank" class="doc-link">
              <i class="fa-duotone fa-file-excel"></i> Accounting Excel
            </a>
          </div>

          <div v-if="selectedRow.error" class="reason-content" style="margin-top: 12px; border-color: #f0d1d5; background: #fff0f1;">
            <strong>Error:</strong> {{ selectedRow.error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
import api from '@/api/api.service'
import auth from "@/authConfig.js"

export default {
  components: { PageHeaderPortal },
  name: 'FhaMipPaymentsDashboard',
  data() {
    return {
      loading: false,
      errorMessage: '',
      rows: [],
      timeFrame: 'last_30_days',
      search: '',
      typeFilter: '',
      statusFilter: '',
      dryRunFilter: '',
      sortBy: 'processedatutc',
      sortDir: 'desc',
      page: 1,
      pageSize: 100,
      showSummaryModal: false,
      selectedRow: {}
    }
  },
  computed: {
    summary() {
      const rows = this.visibleRows || []

      return {
        total: rows.length,
        totalAmount: rows.reduce((sum, x) => sum + (Number(x.amountpaid) || 0), 0),
        monthly: rows.filter(x => x.paymenttype === 'Monthly').length,
        ufmip: rows.filter(x => x.paymenttype === 'UFMIP').length,
        notFound: rows.filter(x => !x.loanfound).length,
        errored: rows.filter(x => !!x.error).length
      }
    },
    visibleRows() {
      return (this.rows || []).filter(x => this.matchesTimeFrame(x?.processedatutc))
    },
    filteredRows() {
      let rows = [...this.visibleRows]

      if (this.search) {
        const term = this.search.toLowerCase()
        rows = rows.filter(x => {
          const haystack = [
            x.loanid,
            x.loannumber,
            x.borrowername,
            x.borrowerfirstname,
            x.borrowerlastname,
            x.loanofficername,
            x.orgid,
            x.casenumber,
            x.trackingid,
            x.paymenttype,
            x.description,
            x.error
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

          return haystack.includes(term)
        })
      }

      if (this.typeFilter) {
        rows = rows.filter(x => x.paymenttype === this.typeFilter)
      }

      if (this.statusFilter === 'written') {
        rows = rows.filter(x => !!x.fieldswritten)
      } else if (this.statusFilter === 'queued') {
        rows = rows.filter(x => !!x.fieldsqueued)
      } else if (this.statusFilter === 'not_found') {
        rows = rows.filter(x => !x.loanfound)
      } else if (this.statusFilter === 'error') {
        rows = rows.filter(x => !!x.error)
      }

      if (this.dryRunFilter === 'dry') {
        rows = rows.filter(x => !!x.dryrun)
      } else if (this.dryRunFilter === 'live') {
        rows = rows.filter(x => !x.dryrun)
      }

      rows.sort((a, b) => {
        const aVal = this.sortValue(a, this.sortBy)
        const bVal = this.sortValue(b, this.sortBy)

        if (aVal === bVal) return 0
        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
        }

        const result = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true,
          sensitivity: 'base'
        })

        return this.sortDir === 'asc' ? result : -result
      })

      return rows
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    pageStart() {
      if (!this.filteredRows.length) return 0
      return (this.page - 1) * this.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.page * this.pageSize, this.filteredRows.length)
    }
  },
  watch: {
    search() { this.page = 1 },
    typeFilter() { this.page = 1 },
    statusFilter() { this.page = 1 },
    dryRunFilter() { this.page = 1 },
    timeFrame() { this.page = 1 },
    filteredRows() {
      if (this.page > this.totalPages) this.page = 1
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    getRequestDays() {
      if (this.timeFrame === 'today') return 2
      if (this.timeFrame === 'yesterday') return 2
      if (this.timeFrame === 'this_week') return 7
      if (this.timeFrame === 'last_week') return 14
      if (this.timeFrame === 'last_7_days') return 7
      if (this.timeFrame === 'last_30_days') return 30
      if (this.timeFrame === 'last_90_days') return 90
      return 30
    },
    startOfDay(date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    },
    endOfDay(date) {
      const d = new Date(date)
      d.setHours(23, 59, 59, 999)
      return d
    },
    startOfWeek(date) {
      const d = this.startOfDay(date)
      const day = d.getDay()
      const diff = day === 0 ? -6 : 1 - day
      d.setDate(d.getDate() + diff)
      return d
    },
    endOfWeek(date) {
      const d = this.startOfWeek(date)
      d.setDate(d.getDate() + 6)
      d.setHours(23, 59, 59, 999)
      return d
    },
    matchesTimeFrame(value) {
      if (!value) return false
      const rowDate = new Date(value)
      if (Number.isNaN(rowDate.getTime())) return false

      const now = new Date()
      const todayStart = this.startOfDay(now)
      const todayEnd = this.endOfDay(now)

      if (this.timeFrame === 'today') return rowDate >= todayStart && rowDate <= todayEnd

      if (this.timeFrame === 'yesterday') {
        const yesterday = new Date(todayStart)
        yesterday.setDate(yesterday.getDate() - 1)
        return rowDate >= this.startOfDay(yesterday) && rowDate <= this.endOfDay(yesterday)
      }

      if (this.timeFrame === 'this_week') return rowDate >= this.startOfWeek(now) && rowDate <= this.endOfWeek(now)

      if (this.timeFrame === 'last_week') {
        const thisWeekStart = this.startOfWeek(now)
        const lastWeekStart = new Date(thisWeekStart)
        lastWeekStart.setDate(lastWeekStart.getDate() - 7)
        const lastWeekEnd = new Date(thisWeekStart)
        lastWeekEnd.setMilliseconds(-1)
        return rowDate >= lastWeekStart && rowDate <= lastWeekEnd
      }

      if (this.timeFrame === 'last_7_days') {
        const start = this.startOfDay(now)
        start.setDate(start.getDate() - 6)
        return rowDate >= start && rowDate <= todayEnd
      }

      if (this.timeFrame === 'last_30_days') {
        const start = this.startOfDay(now)
        start.setDate(start.getDate() - 29)
        return rowDate >= start && rowDate <= todayEnd
      }

      if (this.timeFrame === 'last_90_days') {
        const start = this.startOfDay(now)
        start.setDate(start.getDate() - 89)
        return rowDate >= start && rowDate <= todayEnd
      }

      return true
    },
    async loadData() {
      this.loading = true
      this.errorMessage = ''

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken

        const response = await api.GetFhaMipPaymentQueue(this.getRequestDays(), token)

        this.rows = response?.data?.data || []
      } catch (e) {
        this.rows = []
        this.errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load FHA MIP payment queue.'
      } finally {
        this.loading = false
      }
    },
    borrowerName(row) {
      if (row?.borrowername) return row.borrowername
      const first = row?.borrowerfirstname || ''
      const last = row?.borrowerlastname || ''
      const full = `${first} ${last}`.trim()
      return full || '—'
    },
    statusLabel(row) {
      if (!row) return '—'
      if (!row.loanfound) return 'Loan Not Found'
      if (row.error) return 'Error'
      if (row.fieldsqueued) return 'Queued (Locked)'
      if (row.fieldswritten) return 'Fields Written'
      if (row.dryrun) return 'Dry Run'
      return 'Processed'
    },
    statusClass(row) {
      if (!row) return 'no-audit'
      if (!row.loanfound) return 'no-audit'
      if (row.error) return 'manual-review'
      if (row.fieldsqueued) return 'request-only'
      if (row.fieldswritten) return 'approve'
      return 'request-only'
    },
    formatMoney(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return String(value)
      return d.toLocaleDateString('en-US')
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString('en-US')
    },
    sortValue(row, key) {
      if (key === 'processedatutc') return new Date(row.processedatutc).getTime()
      if (key === 'paymentdate') return row.paymentdate ? new Date(row.paymentdate).getTime() : null
      if (key === 'amountpaid') return Number(row.amountpaid || 0)
      if (key === 'borrowername') return this.borrowerName(row)
      return row[key]
    },
    setSort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortBy = key
      this.sortDir = 'asc'
    },
    sortIcon(key) {
      if (this.sortBy !== key) return ''
      return this.sortDir === 'asc' ? '▲' : '▼'
    },
    prevPage() { if (this.page > 1) this.page-- },
    nextPage() { if (this.page < this.totalPages) this.page++ },
    openSummaryModal(row) {
      this.selectedRow = row || {}
      this.showSummaryModal = true
    },
    closeSummaryModal() {
      this.showSummaryModal = false
      this.selectedRow = {}
    }
  }
}
</script>

<style scoped>
.fha-mip-page {
  padding: 18px;
  background: #f7f8fb;
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  color: #1f2a44;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-icon {
  width: 48px;
  height: 48px;
  border-radius: 5px;
  background: #123a71;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.page-subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #5c6a86;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.refresh-btn {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #123a71;
  border-radius: 5px;
  background: #123a71;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.filter-select {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d5dce8;
  border-radius: 5px;
  background: #fff;
  font-size: 13px;
  color: #1f2a44;
  cursor: pointer;
  outline: none;
}

/* Summary sections */
.summary-sections {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 2fr;
  gap: 12px;
  margin-bottom: 14px;
}

.summary-section {
  background: #ffffff;
  border: 1px solid #e3e8f0;
  border-radius: 7px;
  padding: 12px 14px;
}

.section-header {
  margin-bottom: 10px;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  color: #123a71;
  font-size: 15px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #46536e;
}

.section-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.summary-card {
  border: 1px solid #e3e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fbfcfe;
}

.summary-card-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 6px;
}

.summary-icon {
  font-size: 13px;
  color: #5c6a86;
}

.summary-label {
  font-size: 12px;
  font-weight: 600;
  color: #5c6a86;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2a44;
}

.summary-card.approve { border-color: #cde9d8; background: #f2fbf5; }
.summary-card.request-only { border-color: #cfe0f5; background: #f2f7fd; }
.summary-card.manual-review { border-color: #f5e3c8; background: #fdf8ee; }
.summary-card.no-audit { border-color: #f0d1d5; background: #fdf2f3; }

/* Toolbar */
.toolbar {
  margin-bottom: 12px;
}

.single-line-toolbar {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) auto auto auto;
  gap: 10px;
  align-items: center;
}

.search-input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d5dce8;
  border-radius: 5px;
  font-size: 13px;
  color: #1f2a44;
  background: #fff;
  outline: none;
}

.search-input:focus {
  border-color: #123a71;
}

/* Table card */
.table-card {
  background: #fff;
  border: 1px solid #e3e8f0;
  border-radius: 7px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e9edf4;
}

.table-card-title-wrap {
  display: flex;
  align-items: center;
  gap: 9px;
}

.table-card-icon {
  color: #123a71;
  font-size: 15px;
}

.table-card-title {
  font-size: 14px;
  font-weight: 700;
}

.table-card-count {
  min-width: 34px;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: #eef2f9;
  color: #123a71;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-state {
  padding: 32px 14px;
  text-align: center;
  font-size: 13px;
  color: #5c6a86;
}

.table-state.error {
  color: #b3261e;
}

.table-wrap {
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.locks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.locks-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f4f6fb;
  border-bottom: 1px solid #e2e7f0;
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #46536e;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.locks-table td {
  border-bottom: 1px solid #eef1f7;
  padding: 9px 12px;
  vertical-align: middle;
}

.locks-table tbody tr:hover {
  background: #f8fafd;
}

.primary-text {
  font-weight: 600;
}

.compact-cell {
  white-space: nowrap;
}

.amount-cell {
  white-space: nowrap;
  font-weight: 600;
  text-align: right;
}

/* Pills */
.type-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.type-pill.monthly {
  background: #eaf4ff;
  color: #14549c;
  border: 1px solid #cfe0f5;
}

.type-pill.ufmip {
  background: #f2ecfd;
  color: #5b3aa6;
  border: 1px solid #ded1f5;
}

.dry-run-tag {
  display: inline-flex;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  background: #f4f6fb;
  color: #5c6a86;
  border: 1px solid #dfe4ee;
}

.decision-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.decision-pill.approve { background: #e9f8ef; color: #197a43; border: 1px solid #cde9d8; }
.decision-pill.request-only { background: #eaf4ff; color: #14549c; border: 1px solid #cfe0f5; }
.decision-pill.manual-review { background: #fdf4e5; color: #9c6410; border: 1px solid #f5e3c8; }
.decision-pill.no-audit { background: #fdeef0; color: #a3272f; border: 1px solid #f0d1d5; }

/* Doc buttons */
.docs-cell {
  white-space: nowrap;
}

.doc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 4px;
  border: 1px solid #e0c9cd;
  border-radius: 5px;
  background: #fdf4f5;
  color: #a3272f;
  text-decoration: none;
  font-size: 13px;
  transition: box-shadow 0.12s ease;
}

.doc-btn.excel {
  border-color: #cde9d8;
  background: #f2fbf5;
  color: #197a43;
}

.doc-btn:hover {
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.14);
}

.summary-btn {
  height: 28px;
  padding: 0 12px;
  border: 1px solid #d5dce8;
  border-radius: 5px;
  background: #fff;
  color: #123a71;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.summary-btn:hover {
  background: #f2f5fb;
}

/* Pagination */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 2px 0;
  font-size: 13px;
  color: #5c6a86;
}

.pagination-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d5dce8;
  border-radius: 5px;
  background: #fff;
  color: #1f2a44;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.page-number {
  font-weight: 600;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
}

.modal-card {
  width: min(680px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #e9edf4;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.close-btn {
  border: none;
  background: transparent;
  font-size: 15px;
  cursor: pointer;
  color: #5c6a86;
}

.modal-body {
  padding: 16px 18px;
  overflow: auto;
}

.reason-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 18px;
  font-size: 13px;
  margin-bottom: 14px;
}

.reason-content {
  font-size: 13px;
  line-height: 1.55;
  color: #24314f;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f7f9fc;
  border: 1px solid #e6ebf2;
  border-radius: 5px;
  padding: 14px;
}

.doc-links {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.doc-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border: 1px solid #d5dce8;
  border-radius: 5px;
  background: #fff;
  color: #123a71;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}

.doc-link:hover {
  background: #f2f5fb;
}

@media (max-width: 1600px) {
  .summary-sections { grid-template-columns: 1fr; }
}

@media (max-width: 1200px) {
  .single-line-toolbar { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 900px) {
  .page-header { flex-direction: column; align-items: stretch; }
  .header-actions { width: 100%; flex-wrap: wrap; }
}

@media (max-width: 700px) {
  .fha-mip-page { padding: 16px; }
  .single-line-toolbar { grid-template-columns: 1fr; }
  .reason-meta { grid-template-columns: 1fr; }
  .pagination-bar { flex-direction: column; align-items: flex-start; }
}
</style>
